import React, { useState, useCallback, useRef, useEffect } from 'react';
import Button from '@mui/material/Button';
import { useImmer } from 'use-immer';
import DateInput, { isValidDate } from './DateInput';
import ExpenseTypeSelect from './ExpenseTypeSelect';
import ReimburseeSelect, { isValidReimbursee } from './ReimburseeSelect';
import SupplierSelect, { isValidSupplier, isPaidToBankAccount } from './SupplierSelect';
import TextInput, { isValidText } from './TextInput';
import OcrInput, { isValidOcr } from './OcrInput';
import SupplierInvoiceRows, { isValidSupplierInvoiceRows, supplierInvoiceRowConstructor } from './SupplierInvoiceRows';
import { CollapsibleCurrencySelect, normalizeCurrency } from './CurrencySelect';
import { Attachment, CostCenterItem, Project, ProjectWithExpenseAccounts, Role, Responsible, Draft, Reimbursee, Supplier, DraftRow, DraftToSave, ExpensesData, Narrowable } from './Types';

// The supplier the scan read off the document is the payee only for a supplier invoice
// (attachment Type 0); on a reimbursement receipt (Type 1) it's the shop, not who gets paid.
// Match exactly and unambiguously — org number first, then normalised name — else leave blank
// (a wrong payee is worse than none). Mirrors the Type-1-only reimbursee guess on the backend.
const normalizeOrgNumber = (value: string): string => value.replace(/\D/g, '')
const matchSupplierId = (attachment: Attachment, suppliers: Supplier[]): string | null => {
    if (attachment.Type !== 0) return null
    const org = attachment.SupplierCorporateIdentityNumber
    if (org) {
        const normalized = normalizeOrgNumber(org)
        const hits = suppliers.filter(s => s.CorporateIdentityNumber && normalizeOrgNumber(s.CorporateIdentityNumber) === normalized)
        if (hits.length === 1) return hits[0].Id
    }
    const name = attachment.SupplierName
    if (name) {
        const normalized = name.trim().toLowerCase()
        const hits = suppliers.filter(s => s.Name.trim().toLowerCase() === normalized)
        if (hits.length === 1) return hits[0].Id
    }
    return null
}
import { apiAttestExpenseDraft, apiReadDocument, apiRevokeAttest, apiSaveAndAttestExpenseDraft, apiSaveExpenseDraft } from './Api';
import { addToCallChain, useDebouncedChainedCallback } from './CallCenter';
import ConfirmDialog from './ConfirmDialog';
import { selfLabel, useActiveRoleName } from './ActiveRole';

// Visma's limit on a supplier invoice's payment message (verified against the API, 2026-10-20).
const PAYMENT_MESSAGE_MAX = 25

// A card purchase sends the message as voucher text, which is not capped; the others send it as the
// supplier invoice's payment message. Applied wherever the message can arrive without being typed —
// the comment written at upload in Visma, or a type changed after the text was written — since the
// field's maxLength only stops typing (an underlag was refused with a 28-character comment).
function capMessage(type: number | null, message: string): string {
  return type === 2 ? message : message.slice(0, PAYMENT_MESSAGE_MAX)
}

function getCostCenterItem(costCenterItems: CostCenterItem[], costCenterItemId: string | null): CostCenterItem | undefined {
  return costCenterItems.find(item => item.cost_center_item_id === costCenterItemId)
}

function getProject(costCenterItems: CostCenterItem[], costCenterItemId: string | null, projectId: string | null): Project | null | undefined {
  const costCenterItem = getCostCenterItem(costCenterItems, costCenterItemId)
  if (costCenterItem == undefined) {
      return null
  }
  return costCenterItem.projects.find(project => project.project_id === projectId)
}

// Role names as they are stored, comma-separated. No "och" before the last and no determinate
// form: a role name is a label, not something to inflect into a sentence, and it may already be
// inflected ("Ungdomskommittékassören") or not ("Kassör"). The acting role is named too, marked as
// the reader's own — a list reading "Kassör, mig själv" mixed a name with a pronoun.
function attestantNames(attestants: Set<Role | 'myself'>, selfName: string | null): string {
    return [...attestants].map(a => a === 'myself' ? selfLabel(selfName) : a.name).join(', ')
}


function getRole(otherRoles: Role[], roleId: number): Role | 'myself' {
  const role = otherRoles.find(role => role.id === roleId)
  return role === undefined ? 'myself' : role
}

function MainForm(
  {show, currentDraft, reimbursees, suppliers, costCenterItems, accounts, fiscalYears, otherRoles, onChangeSuppliers, onChangeReimbursees, onChangeData, onDiscardChanges, onSetResponsible, responsible}:
  {
    show: boolean,
    currentDraft: Draft,
    reimbursees: Reimbursee[],
    suppliers: Supplier[],
    costCenterItems: CostCenterItem<ProjectWithExpenseAccounts>[],
    accounts: Record<number, string>,
    fiscalYears: { start: string, end: string }[],
    otherRoles: Role[],
    onChangeSuppliers: (newSuppliers: Supplier[]) => void,
    onChangeReimbursees: (newReimbursees: Reimbursee[]) => void,
    onChangeData: (newData: ExpensesData) => void,
    // Re-render this form from the server's copy, discarding what has been typed. Used to undo an
    // edit the user decided against — nothing has been saved yet, so the copy is still the truth.
    onDiscardChanges: () => void,
    onSetResponsible: (responsible: Responsible) => void,
    responsible: Responsible,
  }
) {
  const initialized = currentDraft.status !== 'uninitialized'
  const attachment = currentDraft.attachment

  const [expenseType, setExpenseType] = useState<number | null>(
    initialized ? currentDraft.type : attachment.Type === 0 ? 0 : currentDraft.probably_payed_by_us ? 2 : 1) // TODO: Clean up!
  const [transactionDate, setTransactionDate] = useState<string>(
    (initialized ? currentDraft.invoice_date : attachment.TransactionDate) ?? '')
  // A saved draft keeps what it holds — its signatures were given on it, and the booking check says
  // if it is too long. A fresh one is offered the upload comment, cut to what Visma accepts.
  const [message, setMessage] = useState(
    initialized ? (currentDraft.message ?? '') : capMessage(expenseType, attachment.Comment ?? ''))
  const [ocrEnabled, setOcrEnabled] = useState(
    initialized ? currentDraft.ocr_enabled : !!attachment.Ocr)
  const [ocrNumber, setOcrNumber] = useState(
    (initialized ? currentDraft.ocr_number : attachment.Ocr) ?? '')
  const [rows, setRows] = useImmer<Narrowable<DraftRow>[]>(
    initialized && currentDraft.rows.length > 0 ? currentDraft.rows : [supplierInvoiceRowConstructor(attachment.TotalAmount || null)])
  const [reimbursee, setReimbursee] = useState(
    initialized ? currentDraft.reimbursee_id : attachment.DefaultReimburseeId)
  const [supplier, setSupplier] = useState(
    initialized ? currentDraft.supplier_id : (matchSupplierId(attachment, suppliers) ?? ''))
  const [invoiceNumber, setInvoiceNumber] = useState(
    (initialized ? currentDraft.invoice_number : attachment.InvoiceNumber) ?? '')
  const [dueDate, setDueDate] = useState(
    (initialized ? currentDraft.due_date : attachment.DueDate) ?? '')
  // The scan's currency is only a guess from Visma's OCR, so it seeds the draft but stays
  // editable; anything without a scanned currency is domestic and defaults to SEK.
  const [currencyCode, setCurrencyCode] = useState(
    normalizeCurrency(initialized ? currentDraft.currency_code : attachment.CurrencyCode))
  const [version, setVersion] = useState(
    initialized ? currentDraft.version : null)
  const [roleIdsAttested, setRoleIdsAttested] = useState<Set<number>>(initialized ? new Set(currentDraft.role_ids_attested) : new Set())

  const changedRef = useRef(false)

  // Did Visma's scan classify this document itself? Type 0 is an invoice and Type 1 a receipt;
  // a document it never read arrives as Type 2. The distinction matters because the form maps
  // both 1 and 2 onto "Kvitto på utlägg", so the selected type alone cannot tell Visma's reading
  // from Eko's fallback.
  const vismaClassified = attachment.Type === 0 || attachment.Type === 1

  // Read what the document itself states and offer it. Visma's scan covers a scanned document
  // well, but leaves OcrNumber empty on every invoice the club receives — and a document it never
  // scanned arrives with nothing at all, defaulting to "Kvitto på utlägg" with every invoice field
  // hidden. That is the case this exists for.
  //
  // Only for a draft nobody has touched: once it is saved, its own stored value is the truth, and
  // refilling a field the user deliberately cleared would be worse than never offering it. `show`
  // gates the request because every draft is mounted at once — fetching on mount would pull every
  // attachment from Visma the moment the tab opens.
  const [documentAsked, setDocumentAsked] = useState(false)
  const messageRef = useRef(message)
  messageRef.current = message
  useEffect(() => {
    if (!show || initialized || documentAsked) return
    setDocumentAsked(true)
    apiReadDocument(currentDraft.draft_id).then(reading => {
      const {ocr_reference, amount, invoice_date, due_date, invoice_number} = reading
      if (ocr_reference !== undefined) {
        setOcrNumber(typed => typed || ocr_reference)
        // Turning the checkbox on is what makes the field usable — it is disabled until then,
        // which is also why a non-empty field already implies the box is ticked. Only when no
        // message has been written, though: enabling OCR disables the message field, and paying by
        // reference or by message is an either/or the user may already have decided.
        if (messageRef.current === '') setOcrEnabled(true)
      }
      // A document naming an account to send money to is an invoice — but only worth inferring
      // where Visma has no opinion of its own. Its scan classifies an attachment as invoice
      // (Type 0) or receipt (Type 1); a document it never scanned arrives as Type 2. The form
      // collapses 1 and 2 to the same "Kvitto på utlägg", so the *attachment's* type has to be
      // consulted rather than the current selection — otherwise a scanned receipt that happens to
      // print a giro would have Visma's own reading overturned. Without the inference, meanwhile,
      // an unscanned invoice stays typed as a receipt, which hides every invoice field below.
      if (reading.asks_to_be_paid && !vismaClassified) {
        setExpenseType(current => current === 1 ? 0 : current)
      }
      // The rest carry no checksum, so they are offered exactly as stated and never over a value
      // that is already there — the scan's, or the user's.
      if (invoice_date !== undefined) setTransactionDate(stated => stated || invoice_date)
      if (due_date !== undefined) setDueDate(stated => stated || due_date)
      if (invoice_number !== undefined) setInvoiceNumber(stated => stated || invoice_number)
      // Never over a scanned amount — but "no scanned amount" reaches us as 0, not null: Visma
      // omits the field entirely and it arrives as a zero once the frame is built. That is why the
      // row seeds from `attachment.TotalAmount || null` above, and why the test here is falsiness
      // rather than null. Only the untouched single row a fresh draft starts with, too: once it
      // has been split across cost centres, the total is no longer any one row's amount.
      if (amount !== undefined && !attachment.TotalAmount) setRows(draft => {
        if (draft.length === 1 && draft[0].amount === null) draft[0].amount = amount
      })
    }).catch(() => {})   // a document Eko can't read just leaves the form as it was
  }, [show, initialized, documentAsked, currentDraft.draft_id])

  const debouncedSave = useDebouncedChainedCallback(
    async () => {
        const draft: DraftToSave = {
          draft_id: currentDraft.draft_id,
          status: 'initialized',
          type: expenseType,
          invoice_date: transactionDate,
          message: message,
          ocr_enabled: ocrEnabled,
          ocr_number: ocrNumber,
          rows: rows,
          reimbursee_id: reimbursee,
          supplier_id: supplier,
          invoice_number: invoiceNumber,
          due_date: dueDate,
          currency_code: currencyCode
        }
        console.log(draft)
        console.log(JSON.stringify(draft))
        await apiSaveExpenseDraft(draft)
        console.log('Sparat!')
    },
    5000
  )

  useEffect(() => {
    if (changedRef.current) {
      debouncedSave()
      changedRef.current = false
    }
    if (!show) {
      debouncedSave.flush()
    }
  })

  // Visma books only into a fiscal year it has. A scan can misread a receipt's year (2025 for 2026),
  // and the refusal came only at booking — after the others had signed — so it is said here instead.
  const inFiscalYear = !isValidDate(transactionDate)
    || fiscalYears.some(year => year.start <= transactionDate.slice(0, 10) && transactionDate.slice(0, 10) <= year.end)
  // An invoice cannot fall due before it is dated, and Visma refuses one that does — again only at
  // the last signature. A wrong year on the invoice itself (förfallodatum 2025 on a 2026 invoice)
  // is what produces it, so it is said here too.
  const dueBeforeInvoiceDate = expenseType == 0 && isValidDate(dueDate) && isValidDate(transactionDate)
    && dueDate.slice(0, 10) < transactionDate.slice(0, 10)

  function isFormValid(): boolean {
    if (!isValidDate(transactionDate) || !inFiscalYear || !isValidSupplierInvoiceRows({costCenterItems: costCenterItems, rows: rows})) {
      return false
    }
 
    if (expenseType == 0 /* faktura */) {
      const ocrOkay = !ocrEnabled || (isValidOcr(ocrNumber) && !paidToBankAccount)
      const messageOkay = ocrEnabled || isValidText(message)
      return ocrOkay && messageOkay && isValidSupplier(supplier, suppliers, currencyCode)
        && isValidText(invoiceNumber) && isValidDate(dueDate) && !dueBeforeInvoiceDate
    } else if (expenseType == 1 /* kvitto pa utlagg */) {
        return isValidText(message) && isValidReimbursee(reimbursee)
    } else /* kvitto pa inkop med klubbens betalkort */ {
      return isValidText(message)
    }
  }

  function saveAndAttestInvoice() {
    debouncedSave.cancel()
    const draft: DraftToSave = {
      draft_id: currentDraft.draft_id,
      status: 'initialized',
      type: expenseType,
      invoice_date: transactionDate,
      message: message,
      ocr_enabled: ocrEnabled,
      ocr_number: ocrNumber,
      rows: rows,
      reimbursee_id: reimbursee,
      supplier_id: supplier,
      invoice_number: invoiceNumber,
      due_date: dueDate,
      currency_code: currencyCode
    }
    console.log(draft)
    console.log(JSON.stringify(draft))
    addToCallChain(
      async () => {
        const responseData = await apiSaveAndAttestExpenseDraft(draft)
        console.log('Sparat och attesterat!')
        onChangeData(responseData)
      },
      true
    )
  }

  function attestInvoice() {
    const data = {
      draft_id: currentDraft.draft_id,
      version: currentDraft.version
    }
    console.log(data)
    addToCallChain(
      async () => {
        const responseData = await apiAttestExpenseDraft(data)
        console.log('Attesterat!')
        onChangeData(responseData)
      },
      true
    )
  }

  function attest() {
    if (version === null) {
      saveAndAttestInvoice()
    }
    else {
      attestInvoice()
    }
  }

  function revokeAttest() {
    addToCallChain(
      async () => { onChangeData(await apiRevokeAttest(currentDraft.draft_id)) },
      true
    )
  }

  // Editing a draft deletes every attest on it — the signature was given on particular content, so
  // changing the content withdraws it. Right, and expensive to do by accident: the cost falls on
  // whoever has to sign again, and they are never told why. So the first edit to an attested draft
  // asks. Held in refs because setChanged is a stable callback used by every field's onChange.
  const attestedRef = useRef(roleIdsAttested)
  useEffect(() => { attestedRef.current = roleIdsAttested }, [roleIdsAttested])
  const askedRef = useRef(false)
  const [confirmingChange, setConfirmingChange] = useState(false)
  const [confirmingAttest, setConfirmingAttest] = useState(false)

  const applyChange = useCallback(() => {
    setVersion(null)
    setRoleIdsAttested(new Set())
    changedRef.current = true
  }, [])

  const setChanged = useCallback(() => {
    // The field's own state is already set, so what was typed stays on screen while the dialog is
    // up — and the attests are *not* cleared yet, so cancelling can put everything back.
    if (!askedRef.current && attestedRef.current.size > 0) {
      setConfirmingChange(true)
      return
    }
    applyChange()
  }, [applyChange])

  const onChangeExpenseType = useCallback((newExpenseType: number) => {
    console.log("onChangeExpenseType")
    setExpenseType(newExpenseType)
    setMessage(m => capMessage(newExpenseType, m))
    setChanged()
  }, [])

  const onChangeTransactionDate = useCallback((newTransactionDate: string) => {
    console.log("onChangeTransactionDate")
    setTransactionDate(newTransactionDate)
    setChanged()
  }, [])

  const onChangeMessage = useCallback((newMessage: string) => {
    console.log("onChangeMessage")
    setMessage(newMessage)
    setChanged()
  }, [])

  const onChangeReimbursee = useCallback((newReimbursee: string | null) => {
    console.log("onChangeReimbursee")
    setReimbursee(newReimbursee)
    setChanged()
  }, [])

  // A deposit to an ordinary bank account carries a message to the payee, never an OCR number. So
  // choosing a supplier paid that way turns OCR off, and the box stays off while it is chosen. An
  // effect rather than the supplier handler, because a supplier just created in the dialog is
  // selected before the refreshed list that says how it is paid has arrived.
  const paidToBankAccount = expenseType == 0 && isPaidToBankAccount(supplier, suppliers)
  useEffect(() => {
    if (paidToBankAccount && ocrEnabled) {
      setOcrEnabled(false)
      setChanged()
    }
  }, [paidToBankAccount])

  const onChangeOcrEnabled = useCallback(() => {
    console.log("onChangeOcrEnabled")
    setOcrEnabled(!ocrEnabled)
    setChanged()
  }, [ocrEnabled])

  const onChangeOcrNumber = useCallback((newOcrNumber: string) => {
    console.log("onChangeOcrNumber")
    setOcrNumber(newOcrNumber)
    setChanged()
  }, [])

  const onChangeCurrency = useCallback((newCurrency: string) => {
    console.log("onChangeCurrency")
    setCurrencyCode(newCurrency)
    // Visma refuses an invoice whose currency differs from its supplier's, and the picker only
    // lists suppliers in the chosen currency — so a supplier picked under the old currency is no
    // longer a valid choice. Clear it rather than let the attest fail at booking time.
    setSupplier('')
    setChanged()
  }, [])

  const onChangeSupplier = useCallback((newSupplier: string | null) => {
    console.log("onChangeSupplier")
    setSupplier(newSupplier)
    setChanged()
  }, [])

  const onChangeInvoiceNumber = useCallback((newInvoiceNumber: string) => {
    console.log("onChangeInvoiceNumber")
    setInvoiceNumber(newInvoiceNumber)
    setChanged()
  }, [])

  const onChangeDueDate = useCallback((newDueDate: string) => {
    console.log("onChangeDueDate")
    setDueDate(newDueDate)
    setChanged()
  }, [])

  const onChangeRows = useCallback((modifier: (rows: DraftRow[]) => void) => {
    console.log("onChangeRows")
    setRows(modifier)
    setChanged()
  }, [])

  const activeRoleName = useActiveRoleName()
  const valid: boolean = isFormValid()

  let remainingAttestants: Set<Role | 'myself'> | null = null
  let alreadyAttested: Set<Role | 'myself'> = new Set()
  let attestButtonEnabled = false
  let isLastAttest = false
  let iHaveAttested = false
  if (valid) {
    const requestedAttestantRoleIds = new Set(
      rows
      .map(row => getProject(costCenterItems, row.cost_center_item_id, row.project_id))
      .filter(project => (project !== null && project !== undefined))
      .flatMap(project => project.attestant_role_ids)
    )
    const remainingAttestantRoleIds = [...requestedAttestantRoleIds].filter(x => !roleIdsAttested.has(x));
    remainingAttestants = new Set(remainingAttestantRoleIds.map(attestant_role_id => getRole(otherRoles, attestant_role_id)))
    attestButtonEnabled = remainingAttestants.has('myself')
    // Last when nobody else is outstanding: this signature is the one that books the draft.
    isLastAttest = remainingAttestants.size === 1 && remainingAttestants.has('myself')
    // Who has signed already, out of the roles this draft's own coding asks for. Kept to that set
    // on purpose: a role that attested and is no longer required (the rows were recoded since) is
    // not part of what is still being waited for, and listing it would read as though it were.
    alreadyAttested = new Set([...requestedAttestantRoleIds].filter(x => roleIdsAttested.has(x))
                              .map(attestant_role_id => getRole(otherRoles, attestant_role_id)))
    iHaveAttested = alreadyAttested.has('myself')
  }

  const ocrLabel = <><input type="checkbox" id="checkbox-ocr-enabled" checked={ocrEnabled} onChange={onChangeOcrEnabled}
                       disabled={paidToBankAccount && !ocrEnabled}
                       title={paidToBankAccount ? 'Till ett bankkonto går bara ett meddelande, inget OCR-nummer.' : undefined}/>
                &nbsp;OCR-nummer</>

  return (
    show &&
    <div id="div-recognize-main-form">
      <div id="div-recognize-common-part">
        <ExpenseTypeSelect id="type-selector" label="Typ" value={expenseType} onChange={onChangeExpenseType}/>
        <div>
          <DateInput id="transaction-date" label='Datum' value={transactionDate} onChange={onChangeTransactionDate}/>
          {!inFiscalYear &&
            <div id="no-fiscal-year" style={{ fontSize: '0.8rem', color: '#c62828' }}>
              Det finns inget räkenskapsår i Visma för {transactionDate.slice(0, 10)}. Kontrollera datumet.
            </div>}
        </div>
        <SupplierInvoiceRows costCenterItems={costCenterItems} accounts={accounts} rows={rows} onChangeRows={onChangeRows}
          currencyCode={expenseType == 0 ? currencyCode : 'SEK'}/>
        {/* An invoice and a kvitto på utlägg send this to Visma as the supplier invoice's payment
            message, which Visma caps at 25 characters ("Message mustn't be more than 25 characters");
            a card purchase uses it as the voucher text, which is not capped. */}
        <TextInput id="message" label="Meddelande" disabled={expenseType == 0 && ocrEnabled} value={message}
          onChange={onChangeMessage} inputProps={expenseType == 2 ? undefined : {'maxLength': PAYMENT_MESSAGE_MAX}}/>
      </div>
      { expenseType == 1 &&
      <div id="div-receipt">
        <ReimburseeSelect id="reimbursee-id" label="Den som ska få pengar tillbaka" reimbursees={reimbursees}
          value={reimbursee} onChange={onChangeReimbursee} onChangeReimbursees={onChangeReimbursees}/>
      </div>
      }
      { expenseType == 0 &&
        <div id="div-invoice">
          <div className="label-and-input">
              <OcrInput disabled={!ocrEnabled} label={ocrLabel} name="ocr_number" id="ocr-number"
                value={ocrNumber} onChange={onChangeOcrNumber}/>
              {paidToBankAccount &&
                <div id="ocr-bank-account-note" style={{ fontSize: '0.8rem', color: '#666' }}>
                  Leverantören får betalt till ett bankkonto, och dit går bara ett meddelande.
                </div>}
          </div>
          <div className="label-and-input">
              <CollapsibleCurrencySelect id="currency-code" label="Valuta" value={currencyCode} onChange={onChangeCurrency}/>
          </div>
          <div id="div-supplier">
              <div className="label-and-input">
                  <SupplierSelect
                      label="Leverantör"
                      id="supplier-id"
                      suppliers={suppliers}
                      currencyCode={currencyCode}
                      usesOcr={ocrEnabled}
                      attachmentId={currentDraft.draft_id}
                      value={supplier}
                      onChange={onChangeSupplier}
                      onChangeCurrency={onChangeCurrency}
                      onChangeSuppliers={onChangeSuppliers}
                  />
              </div>
          </div>
          <div id="div-common-for-invoice">
              <div className="label-and-input">
                  <TextInput label="Fakturanummer" value={invoiceNumber} onChange={onChangeInvoiceNumber} name="invoice_number" id="invoice-number"/>
              </div>
              <div className="label-and-input">
                  <DateInput label="Förfallodatum" id="due-date" value={dueDate} onChange={onChangeDueDate}/>
                  {dueBeforeInvoiceDate &&
                    <div id="due-before-invoice-date" style={{ fontSize: '0.8rem', color: '#c62828' }}>
                      Förfallodatumet ligger före fakturans datum ({transactionDate.slice(0, 10)}). Kontrollera året.
                    </div>}
              </div>
          </div>
        </div>
      }
      {/* <FinishedInput label='Klar att attesteras' value={finished} onChange={onChangeFinished}/>*/}
      {/* Two rows, and who has signed comes first: the reader's question is where the draft stands.
          The roles used to be bare spans with no separator, so three attestants read as
          "KassörUngdomsledarenmig själv". Only the outstanding names are bold — they are the ones
          being waited for, and the line above is settled business. */}
      {alreadyAttested.size > 0 &&
        <div>Har attesterat: {attestantNames(alreadyAttested, activeRoleName)}</div>
      }
      {remainingAttestants && remainingAttestants.size > 0 &&
        <div>Attest krävs från: <strong>{attestantNames(remainingAttestants, activeRoleName)}</strong></div>
      }
      {/* One-click hand-off: when exactly one attestant remains and it isn't me, pass the baton to
          them (sets responsible) instead of opening the "titta härnäst"-picker. */}
      {(() => {
        const sole = remainingAttestants && remainingAttestants.size === 1 ? [...remainingAttestants][0] : null
        // Hide when the sole attestant already holds the baton — sending to them would be a no-op.
        return sole && sole !== 'myself' && sole.id !== responsible &&
          <Button variant="outlined" onClick={() => onSetResponsible(sole.id)}>Skicka till {sole.name}</Button>
      })()}
      {/* The ellipsis promises a dialog, so it appears only where there is one: attesting last
          books the draft and cannot be undone, while any other attest can be taken back with the
          button beside it. */}
      <Button variant="contained" disabled={!attestButtonEnabled}
              onClick={() => isLastAttest ? setConfirmingAttest(true) : attest()}>
          {isLastAttest ? 'Attestera…' : 'Attestera'}
      </Button>
      {iHaveAttested &&
          <Button variant="outlined" onClick={revokeAttest}>Ta tillbaka min attest</Button>}
      {/* Said without a word about bookkeeping. What this attestant needs to know is that the
          decision is final and theirs — that Eko then writes it into Visma is true, and not
          something to make a volunteer responsible for understanding. */}
      <ConfirmDialog
          open={confirmingAttest}
          title="Du är sista attestant"
          confirmLabel="Attestera"
          message="Underlaget blir klart och kan inte ändras eller tas tillbaka."
          onCancel={() => setConfirmingAttest(false)}
          onConfirm={() => { setConfirmingAttest(false); attest() }}/>
      <ConfirmDialog
          open={confirmingChange}
          title="Underlaget är redan attesterat"
          confirmLabel="Ändra ändå"
          cancelLabel="Ångra ändringen"
          message={<>
              <p>
                  {alreadyAttested.size > 0
                      ? `${attestantNames(alreadyAttested, activeRoleName)} har attesterat detta underlag.`
                      : 'Underlaget är attesterat.'} Om du ändrar något måste det attesteras igen.
              </p>
              <p>Attesten tas bort när ändringen sparas.</p>
          </>}
          onCancel={() => { setConfirmingChange(false); onDiscardChanges() }}
          onConfirm={() => { askedRef.current = true; setConfirmingChange(false); applyChange() }}/>
    </div>
  )
}

export default MainForm
